import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import accountingService from '@api/v1/accountingService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'

const TABS = [
  { id: 'coa', label: 'Chart of Accounts' },
  { id: 'journal', label: 'Journal Entries' },
  { id: 'trial', label: 'Trial Balance' },
  { id: 'ar', label: 'Accounts Receivable' },
  { id: 'checks', label: 'Checks' },
]

/**
 * CC-53132 — the surface a read that NEVER ANSWERED gets, on all six of this
 * page's reads.
 *
 * Every panel here used to branch on `isLoading` / `error` / `length === 0`, and
 * `isLoading` is `isPending && isFetching` — FALSE for a query that is pending
 * but not in flight. The two ways that happens are `fetchStatus === 'paused'`
 * (react-query's default `networkMode: 'online'` refusing to fire while
 * `onlineManager` believes the browser is offline: `queryFn` is never called,
 * nothing throws, nothing logs, `data` stays `undefined`) and `enabled: false`.
 * The `?.data || []` on every one of these reads then turned that `undefined`
 * into `[]`, and the panel announced "No outstanding invoices", "No transactions
 * found", "Journal Entries (0)".
 *
 * On a general ledger that is not a cosmetic bug. "This account has no
 * transactions" and "we never asked" are different facts, and only one of them
 * is a reason to stop looking. So the unresolved states get their own surface,
 * saying in as many words that the screen is NOT describing the firm's books,
 * and carrying the retry that the empty state gives no reason to look for.
 *
 * `role="alert"` on both tones: a user who was just told there are no unpaid
 * invoices has to be interrupted, not left to notice a colour change.
 *
 * One component rather than ten copies — six reads × two tones would otherwise
 * be ~150 lines of near-identical JSX, and the copy is the only part that is
 * genuinely per-read (an accounts list and an account register are not empty in
 * the same way, so they cannot be reassured in the same words).
 */
const ReadNotAnswered = ({ tone, headline, detail, onRetry }) => {
  const failed = tone === 'failed'
  return (
    <div
      role="alert"
      className={`m-5 rounded-xl border p-6 text-sm ${
        failed ? 'border-red-100 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-900'
      }`}
    >
      <p className="font-medium">{headline}</p>
      <p className={`mt-1 ${failed ? '' : 'text-amber-800'}`}>{detail}</p>
      <button
        type="button"
        onClick={onRetry}
        className={`mt-3 rounded-lg border bg-white px-3 py-1.5 text-xs font-medium ${
          failed
            ? 'border-red-200 text-red-700 hover:bg-red-100'
            : 'border-amber-300 text-amber-900 hover:bg-amber-100'
        }`}
      >
        Try again
      </button>
    </div>
  )
}

const Accounting = () => {
  useDocumentTitle('Accounting')
  // CC-49837: /accounting?account=<id> deep-links from the financial
  // statements straight into the Chart of Accounts register for that account.
  const [searchParams] = useSearchParams()
  const linkedAccountId = searchParams.get('account')
  const [activeTab, setActiveTab] = useState('coa')

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Accounting</h1>
        <p className="text-sm text-gray-500 mt-1">General ledger, journal entries, and financial reports</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'coa' && <ChartOfAccountsTab initialAccountId={linkedAccountId} />}
      {activeTab === 'journal' && <JournalEntriesTab />}
      {activeTab === 'trial' && <TrialBalanceTab />}
      {activeTab === 'ar' && <AccountsReceivableTab />}
      {activeTab === 'checks' && <ChecksTab />}
    </div>
  )
}

// ─── Chart of Accounts ──────────────────────────────────────────────────────

const ChartOfAccountsTab = ({ initialAccountId = null }) => {
  const [category, setCategory] = useState('0')
  // CC-49837: preselect the deep-linked account so its register loads
  // immediately. Account ids are numeric server-side; keep the raw string —
  // the register endpoint takes it verbatim.
  const [selectedAccountId, setSelectedAccountId] = useState(initialAccountId)

  const accountsQuery = useQuery({
    queryKey: ['accounts', category],
    queryFn: () => accountingService.getAccounts(category),
  })
  // No `enabled` option, so this query can never sit at `idle` — it is folded
  // into the loading branch below. (Covered rather than omitted: an unhandled
  // state renders NOTHING, so whoever adds an `enabled:` gate here later would
  // be paid in a blank panel.)
  const accountsState = useReadState(`accounts:${category}`, accountsQuery)
  const accountsReady = accountsState === 'ready'

  const registerQuery = useQuery({
    queryKey: ['account-register', selectedAccountId],
    queryFn: () => accountingService.getAccountRegister(selectedAccountId),
    enabled: !!selectedAccountId,
  })
  // This one IS conditionally enabled, so `idle` is reachable and is the
  // ordinary "you haven't picked an account yet" — NOT an error, and NOT
  // loading. It is rendered by the pre-existing `!selectedAccountId` branch
  // below, which is the same condition by construction; folding it into the
  // spinner instead would leave a permanent spinner beside an untouched
  // account list.
  const registerState = useReadState(`account-register:${selectedAccountId ?? 'none'}`, registerQuery)
  const registerReady = registerState === 'ready'

  // Coerced only so the `.map()` below has something to iterate. `accountsReady`
  // / `registerReady` are what decide whether an empty one may be DESCRIBED to
  // the user — that is the half that was wrong, not the iteration.
  const accounts = accountsQuery.data?.data ?? []
  const register = registerQuery.data?.data ?? []

  const categories = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Asset' },
    { value: '2', label: 'Liability' },
    { value: '3', label: 'Equity' },
    { value: '4', label: 'Income' },
    { value: '5', label: 'Expense' },
  ]

  return (
    <div className="space-y-6">
      {/* Filter */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-500">Category:</label>
        {categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => { setCategory(cat.value); setSelectedAccountId(null) }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              category === cat.value ? 'bg-cb-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Accounts List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            {/* The count comes off the heading entirely until the read lands.
                "Accounts (0)" beside a paused query is a claim about the firm's
                chart of accounts, not an observation, and it is the only number
                on this panel — nothing else here would have contradicted it. */}
            <h2 className="text-base font-semibold text-gray-900">
              Accounts{accountsReady ? ` (${accounts.length})` : ''}
            </h2>
          </div>
          {accountsState === 'failed' ? (
            /* Failed first, mirroring `readState`'s own precedence: a query can
               hold stale `data` alongside a fresh error, and a stale success
               must not mask a live failure. The sentence is the original one; a
               retry and an announcement are what it was missing. */
            <ReadNotAnswered
              tone="failed"
              headline="Failed to load accounts"
              detail="Nothing was read, so this is not a chart of accounts with nothing in it."
              onRetry={() => accountsQuery.refetch()}
            />
          ) : accountsState === 'blocked' ? (
            <ReadNotAnswered
              tone="blocked"
              headline="Can’t reach the server, so we haven’t loaded your accounts."
              detail="This is not an empty chart of accounts — the request was never sent. Your connection looks offline; check it and try again."
              onRetry={() => accountsQuery.refetch()}
            />
          ) : !accountsReady ? (
            <div className="p-8 flex justify-center"><LoadingSpinner message="Loading accounts..." /></div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Account</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {accounts.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => setSelectedAccountId(a.id)}
                      className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                        String(selectedAccountId) === String(a.id) ? 'bg-cb-light' : ''
                      } ${a.isInactive ? 'opacity-50' : ''}`}
                    >
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-gray-900">{a.name}</p>
                        {a.accountNumber && <p className="text-xs text-gray-400">#{a.accountNumber}</p>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{a.type}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-900">{a.balanceFormatted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Account Register */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">
              {selectedAccountId ? 'Account Register' : 'Select an Account'}
            </h2>
          </div>
          {/* `idle` — the query is disabled until an account is picked. Left
              exactly as it was: "not asked yet" is the truth here and the user
              is the one who hasn't asked, so it is neither an error nor a
              spinner. */}
          {!selectedAccountId ? (
            <p className="p-8 text-sm text-gray-500 text-center">Click an account to view transactions</p>
          ) : registerState === 'failed' ? (
            /* This branch is NEW. The register read had no error render site at
               all — only `registerLoading` — so a rejected read fell straight
               through to "No transactions found". On an account register that
               reads as "this account has never been posted to", which is the
               single most misleading thing this page could say. */
            <ReadNotAnswered
              tone="failed"
              headline="Failed to load this account’s register"
              detail="No transactions were read, so this is not an account with no activity."
              onRetry={() => registerQuery.refetch()}
            />
          ) : registerState === 'blocked' ? (
            <ReadNotAnswered
              tone="blocked"
              headline="Can’t reach the server, so we haven’t loaded this register."
              detail="This account may well have transactions — the request was never sent. Your connection looks offline; check it and try again."
              onRetry={() => registerQuery.refetch()}
            />
          ) : !registerReady ? (
            <div className="p-8 flex justify-center"><LoadingSpinner message="Loading register..." /></div>
          ) : register.length === 0 ? (
            <p className="p-8 text-sm text-gray-500 text-center">No transactions found</p>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Reference</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Debit</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {register.map((t, i) => (
                    <tr key={i} className="hover:bg-gray-50/50">
                      <td className="px-4 py-2.5 text-gray-600">{t.date}</td>
                      <td className="px-4 py-2.5 text-gray-900">{t.reference || t.type || '—'}</td>
                      <td className="px-4 py-2.5 text-right text-gray-900">{t.debit > 0 ? t.debitFormatted : ''}</td>
                      <td className="px-4 py-2.5 text-right text-gray-900">{t.credit > 0 ? t.creditFormatted : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Journal Entries ─────────────────────────────────────────────────────────

const JournalEntriesTab = () => {
  const [type, setType] = useState('None')

  const entriesQuery = useQuery({
    queryKey: ['journal-entries', type],
    queryFn: () => accountingService.getJournalEntries({ type }),
  })
  // No `enabled` option → `idle` is unreachable, so it rides with `loading`.
  const entriesState = useReadState(`journal-entries:${type}`, entriesQuery)
  const entriesReady = entriesState === 'ready'

  const entries = entriesQuery.data?.data ?? []
  const types = ['None', 'Manual', 'Automatic']

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        {/* Count withheld until the read lands — see the heading on Accounts. */}
        <h2 className="text-base font-semibold text-gray-900">
          Journal Entries{entriesReady ? ` (${entries.length})` : ''}
        </h2>
        <div className="flex gap-2">
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`px-3 py-1 rounded text-xs font-medium ${
                type === t ? 'bg-cb-navy text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {t === 'None' ? 'All' : t}
            </button>
          ))}
        </div>
      </div>
      {entriesState === 'failed' ? (
        <ReadNotAnswered
          tone="failed"
          headline="Failed to load journal entries"
          detail="Nothing was read, so this is not a period with no journal entries."
          onRetry={() => entriesQuery.refetch()}
        />
      ) : entriesState === 'blocked' ? (
        <ReadNotAnswered
          tone="blocked"
          headline="Can’t reach the server, so we haven’t loaded your journal entries."
          detail="This is not a ledger with no entries — the request was never sent. Your connection looks offline; check it and try again."
          onRetry={() => entriesQuery.refetch()}
        />
      ) : !entriesReady ? (
        <div className="p-8 flex justify-center"><LoadingSpinner message="Loading..." /></div>
      ) : entries.length === 0 ? (
        <p className="p-8 text-sm text-gray-500 text-center">No journal entries found</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ref #</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Memo</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Debit</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {entries.map((e) => (
              <tr key={e.id} className="hover:bg-gray-50/50">
                <td className="px-5 py-3 text-gray-600">{e.date}</td>
                <td className="px-5 py-3 text-gray-900 font-medium">{e.refNum || '—'}</td>
                <td className="px-5 py-3 text-gray-600">{e.type}</td>
                <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{e.memo || '—'}</td>
                <td className="px-5 py-3 text-right text-gray-900">{e.debitTotalFormatted}</td>
                <td className="px-5 py-3 text-right text-gray-900">{e.creditTotalFormatted}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── Trial Balance ───────────────────────────────────────────────────────────

const TrialBalanceTab = () => {
  const trialQuery = useQuery({
    queryKey: ['trial-balance'],
    queryFn: () => accountingService.getTrialBalance(),
  })
  // No `enabled` option → `idle` is unreachable, so it rides with `loading`.
  const trialState = useReadState('trial-balance', trialQuery)
  const trialReady = trialState === 'ready'

  const accounts = trialQuery.data?.data?.accounts ?? []
  // `totals` is rendered in the footer row, which only exists inside the ready
  // branch — so a paused read can no longer print a debit/credit total either.
  const totals = trialQuery.data?.data?.totals ?? {}

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">Trial Balance</h2>
      </div>
      {trialState === 'failed' ? (
        <ReadNotAnswered
          tone="failed"
          headline="Failed to load trial balance"
          detail="Nothing was read, so the ledger has not been shown to be empty or out of balance."
          onRetry={() => trialQuery.refetch()}
        />
      ) : trialState === 'blocked' ? (
        /* "No data" is a particularly bad thing for a trial balance to say
           wrongly — it is the report a firm reads to confirm the books balance,
           so an unasked read must not be able to imitate a clean one. */
        <ReadNotAnswered
          tone="blocked"
          headline="Can’t reach the server, so we haven’t loaded your trial balance."
          detail="This is not a ledger with no data — the request was never sent. Your connection looks offline; check it and try again."
          onRetry={() => trialQuery.refetch()}
        />
      ) : !trialReady ? (
        <div className="p-8 flex justify-center"><LoadingSpinner message="Loading..." /></div>
      ) : accounts.length === 0 ? (
        <p className="p-8 text-sm text-gray-500 text-center">No data</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Account</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Debit</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {accounts.map((a) => (
              <tr key={a.id} className="hover:bg-gray-50/50">
                <td className="px-5 py-3 text-gray-900 font-medium">{a.name}</td>
                <td className="px-5 py-3 text-gray-600">{a.type}</td>
                <td className="px-5 py-3 text-right text-gray-900">{a.debit > 0 ? a.debitFormatted : ''}</td>
                <td className="px-5 py-3 text-right text-gray-900">{a.credit > 0 ? a.creditFormatted : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
              <td colSpan={2} className="px-5 py-3 text-gray-900">Totals</td>
              <td className="px-5 py-3 text-right text-gray-900">{totals.debit}</td>
              <td className="px-5 py-3 text-right text-gray-900">{totals.credit}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  )
}

// ─── Accounts Receivable ─────────────────────────────────────────────────────

const AccountsReceivableTab = () => {
  const arQuery = useQuery({
    queryKey: ['accounts-receivable'],
    queryFn: () => accountingService.getAccountsReceivable(),
  })
  // No `enabled` option → `idle` is unreachable, so it rides with `loading`.
  const arState = useReadState('accounts-receivable', arQuery)
  const arReady = arState === 'ready'

  const invoices = arQuery.data?.data ?? []
  const totalBalance = invoices.reduce((sum, inv) => sum + (inv.balance || 0), 0)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Accounts Receivable</h2>
          {/* The worst count on the page, and it rendered OUTSIDE every branch
              below — so a paused read printed "0 outstanding invoices — $0.00"
              above a panel that was still deciding what to say. A firm reading
              that concludes it is fully collected. Both the count and the money
              wait for a completed read; the heading alone carries the panel
              until then. */}
          {arReady && (
            <p className="text-xs text-gray-500 mt-0.5">{invoices.length} outstanding invoices — ${totalBalance.toFixed(2)}</p>
          )}
        </div>
      </div>
      {arState === 'failed' ? (
        <ReadNotAnswered
          tone="failed"
          headline="Failed to load AR data"
          detail="No invoices were read, so this is not a firm with nothing outstanding."
          onRetry={() => arQuery.refetch()}
        />
      ) : arState === 'blocked' ? (
        /* "No outstanding invoices" is the sentence this whole ticket is about:
           a firm that believes it has collected everything stops chasing. Say
           plainly that nothing was read. */
        <ReadNotAnswered
          tone="blocked"
          headline="Can’t reach the server, so we haven’t loaded your receivables."
          detail="This is not a firm with nothing outstanding — the request was never sent. Your connection looks offline; check it and try again."
          onRetry={() => arQuery.refetch()}
        />
      ) : !arReady ? (
        <div className="p-8 flex justify-center"><LoadingSpinner message="Loading..." /></div>
      ) : invoices.length === 0 ? (
        <p className="p-8 text-sm text-gray-500 text-center">No outstanding invoices</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Invoice</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Matter</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Paid</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {invoices.map((inv) => (
              <tr key={inv.id} className="hover:bg-gray-50/50">
                <td className="px-5 py-3 text-gray-900 font-medium">{inv.invoiceNumber || inv.invoiceName || '—'}</td>
                <td className="px-5 py-3 text-gray-600">{inv.matterName || '—'}</td>
                <td className="px-5 py-3 text-gray-600">{inv.clientName || '—'}</td>
                <td className="px-5 py-3 text-gray-600">{inv.issueDate || '—'}</td>
                <td className="px-5 py-3 text-right text-gray-900">${(inv.total || 0).toFixed(2)}</td>
                <td className="px-5 py-3 text-right text-gray-600">${(inv.payments || 0).toFixed(2)}</td>
                <td className="px-5 py-3 text-right font-semibold text-gray-900">${(inv.balance || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── Checks ──────────────────────────────────────────────────────────────────

const ChecksTab = () => {
  const checksQuery = useQuery({
    queryKey: ['checks'],
    queryFn: () => accountingService.getChecks(),
  })
  // No `enabled` option → `idle` is unreachable, so it rides with `loading`.
  const checksState = useReadState('checks', checksQuery)
  const checksReady = checksState === 'ready'

  const checks = checksQuery.data?.data ?? []

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        {/* Count withheld until the read lands — see the heading on Accounts. */}
        <h2 className="text-base font-semibold text-gray-900">
          Checks{checksReady ? ` (${checks.length})` : ''}
        </h2>
      </div>
      {checksState === 'failed' ? (
        <ReadNotAnswered
          tone="failed"
          headline="Failed to load checks"
          detail="Nothing was read, so this is not an account with no checks written against it."
          onRetry={() => checksQuery.refetch()}
        />
      ) : checksState === 'blocked' ? (
        <ReadNotAnswered
          tone="blocked"
          headline="Can’t reach the server, so we haven’t loaded your checks."
          detail="This is not an account with no checks — the request was never sent. Your connection looks offline; check it and try again."
          onRetry={() => checksQuery.refetch()}
        />
      ) : !checksReady ? (
        <div className="p-8 flex justify-center"><LoadingSpinner message="Loading..." /></div>
      ) : checks.length === 0 ? (
        <p className="p-8 text-sm text-gray-500 text-center">No checks found</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Check #</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Matter</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
              <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {checks.map((ck) => (
              <tr key={ck.id} className="hover:bg-gray-50/50">
                <td className="px-5 py-3 text-gray-900 font-medium">{ck.checkNumber || '—'}</td>
                <td className="px-5 py-3 text-gray-600">{ck.date}</td>
                <td className="px-5 py-3 text-gray-600">{ck.matterName || '—'}</td>
                <td className="px-5 py-3 text-gray-600">{ck.type || ck.checkType || '—'}</td>
                <td className="px-5 py-3 text-right text-gray-900 font-medium">{ck.amountFormatted}</td>
                <td className="px-5 py-3 text-center">
                  {ck.voided ? (
                    <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded font-medium">Voided</span>
                  ) : (
                    <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium">Active</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default Accounting
